import type { MutableRefObject } from "react";
import type {
	RolePermissionsMatrixApiResponse,
	PermissionContextDto,
} from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import type { PermissionsSupportClientMatrixViewBody } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissionsTypes";
import { RolePermissions } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { getRolePermissionsNotaryInstance } from "@Front/Api/LeCoffreApi/Notary/RolePermissions/RolePermissionsFactory";
import {
	getTokenFromConnectedStoreByContext,
	resolveAuthContextForRolePermissionsRefresh,
	resolveNotaryAccessTokenFromSession,
} from "@Front/Api/helpers/authTokenResolutionHelpers";
import { getLeCoffreAccessTokenCookieForContext } from "@Front/Utils/lecoffreSessionCookieHelpers";
import { getActiveOfficeStore } from "@Front/Stores/ActiveOfficeStore";
import { computePermissionsSupportDiagnosticsSnapshot } from "./rolePermissionsMatrixCheck";
import { logMatrixResponse, logMatrixStateUpdate, handleRefreshError } from "./useRolePermissionsControllerHelpers";
import { isSuperAdminRole } from "@Front/Utils/officeRoleStatusHelpers";
import { jwtDecode } from "@Front/Utils/jwtDecodeWrapper";
import { maybeRefreshNotaryJwtAfterLiveSubscriptionMismatch } from "@Front/Utils/maybeRefreshNotaryJwtAfterLiveSubscriptionMismatch";

export type Matrix = RolePermissionsMatrixApiResponse | null;
export const REFRESH_DEBOUNCE_MS = 500;

type SessionJwtPayload = {
	userId?: string;
	customerId?: string;
	uid?: string;
	email?: string | null;
	folderUid?: string | null;
	role?: string | null;
	userType?: string | null;
};

export type RefreshExecutorParams = {
	isRefreshingRef: MutableRefObject<boolean>;
	lastRefreshRef: MutableRefObject<number>;
	setMatrix: (value: Matrix) => void;
	setPermissionContext: (value: PermissionContextDto | null) => void;
	setLoading: (value: boolean) => void;
	setError: (value: string | null) => void;
};

function getBrowserPathname(): string | null {
	if (typeof window === "undefined") {
		return null;
	}
	return window.location.pathname;
}

function shouldLoadSuperAdminFullMatrix(): boolean {
	return getBrowserPathname() === "/super-admin/role-permissions";
}

export function clearMatrixWhenNoAccessToken(params: {
	setMatrix: (value: Matrix) => void;
	setError: (value: string | null) => void;
	setLoading: (value: boolean) => void;
	setPermissionContext: (value: PermissionContextDto | null) => void;
}): void {
	const { setMatrix, setError, setLoading, setPermissionContext } = params;
	queueMicrotask(() => {
		setMatrix(null);
		setError(null);
		setLoading(false);
		setPermissionContext(null);
		getActiveOfficeStore().syncActiveOfficeFromContext(null);
	});
}

async function fetchNotaryMatrixAndContext(): Promise<{
	matrixResponse: RolePermissionsMatrixApiResponse;
	permissionContextResponse: PermissionContextDto | null;
}> {
	const notaryService = getRolePermissionsNotaryInstance();
	const apiResponse = await notaryService.getMyPermissions();
	return {
		matrixResponse: apiResponse?.matrix ?? {},
		permissionContextResponse: apiResponse?.context ?? null,
	};
}

async function mergeSuperAdminMatrixIfNeeded(
	matrixResponse: RolePermissionsMatrixApiResponse,
	permissionContextResponse: PermissionContextDto | null,
	effectiveRole: string | null,
): Promise<RolePermissionsMatrixApiResponse> {
	if (!isSuperAdminRole(effectiveRole)) {
		logMatrixResponse(matrixResponse, permissionContextResponse, effectiveRole);
		return matrixResponse;
	}
	if (!shouldLoadSuperAdminFullMatrix()) {
		return matrixResponse;
	}
	return await RolePermissions.getInstance().getMatrix();
}

function buildSessionPermissionContext(activeContext: "customer" | "thirdparty", accessToken: string): PermissionContextDto | null {
	try {
		const payload = jwtDecode<SessionJwtPayload>(accessToken);
		const userId = payload.userId ?? payload.customerId ?? payload.uid ?? null;
		const role = activeContext === "customer" ? "customer" : "thirdparty";
		return {
			userId,
			globalRole: role,
			userType: payload.userType ?? role,
			primaryOfficeUid: null,
			activeOfficeUid: null,
			email: payload.email ?? undefined,
			folderUid: payload.folderUid ?? null,
			isGuest: false,
			scopes: [],
			acl: [],
		};
	} catch {
		return null;
	}
}

function commitMatrixState(input: {
	matrixResponse: RolePermissionsMatrixApiResponse;
	permissionContextResponse: PermissionContextDto | null;
	setMatrix: (value: Matrix) => void;
	setPermissionContext: (value: PermissionContextDto | null) => void;
	setLoading: (value: boolean) => void;
}): void {
	const { matrixResponse, permissionContextResponse, setMatrix, setPermissionContext, setLoading } = input;
	queueMicrotask(() => {
		logMatrixStateUpdate(matrixResponse, permissionContextResponse);
		setMatrix(matrixResponse);
		setPermissionContext(permissionContextResponse);
		getActiveOfficeStore().syncActiveOfficeFromContext(permissionContextResponse?.activeOfficeUid ?? null);
		setLoading(false);
		schedulePermissionsSupportClientLog(matrixResponse, permissionContextResponse);
	});
}

function schedulePermissionsSupportClientLog(
	matrixResponse: RolePermissionsMatrixApiResponse,
	permissionContextResponse: PermissionContextDto | null,
): void {
	if (typeof window === "undefined") {
		return;
	}
	queueMicrotask(() => {
		void postPermissionsSupportClientMatrixViewFromBrowser(matrixResponse, permissionContextResponse).catch(() => {
			/* support client view is best-effort */
		});
	});
}

async function postPermissionsSupportClientMatrixViewFromBrowser(
	matrixResponse: RolePermissionsMatrixApiResponse,
	permissionContextResponse: PermissionContextDto | null,
): Promise<void> {
	const stakeholders = computePermissionsSupportDiagnosticsSnapshot(matrixResponse, permissionContextResponse);
	const notary = getRolePermissionsNotaryInstance();
	await notary.postPermissionsSupportClientView({
		routePath: window.location.pathname,
		receivedAtIso: new Date().toISOString(),
		contextSnapshot: buildClientMatrixViewContextSnapshot(permissionContextResponse),
		stakeholdersFromClientMatrix: stakeholders,
	});
}

function buildClientMatrixViewContextSnapshot(
	permissionContextResponse: PermissionContextDto | null,
): PermissionsSupportClientMatrixViewBody["contextSnapshot"] {
	if (!permissionContextResponse) {
		return {
			userId: null,
			globalRole: null,
			primaryOfficeUid: null,
			activeOfficeUid: null,
			isGuest: false,
			officeRole: null,
		};
	}
	return {
		userId: permissionContextResponse.userId,
		globalRole: permissionContextResponse.globalRole,
		primaryOfficeUid: permissionContextResponse.primaryOfficeUid,
		activeOfficeUid: permissionContextResponse.activeOfficeUid,
		isGuest: permissionContextResponse.isGuest,
		officeRole: permissionContextResponse.officeRole ?? null,
	};
}

async function fetchMatrixAndCommitState(input: {
	setMatrix: (value: Matrix) => void;
	setPermissionContext: (value: PermissionContextDto | null) => void;
	setLoading: (value: boolean) => void;
}): Promise<void> {
	let { matrixResponse: initialMatrix, permissionContextResponse } = await fetchNotaryMatrixAndContext();
	const jwtRefreshed = await maybeRefreshNotaryJwtAfterLiveSubscriptionMismatch(permissionContextResponse);
	if (jwtRefreshed) {
		const refetched = await fetchNotaryMatrixAndContext();
		initialMatrix = refetched.matrixResponse;
		permissionContextResponse = refetched.permissionContextResponse;
	}
	const effectiveRole = permissionContextResponse?.globalRole ?? null;
	const matrixResponse = await mergeSuperAdminMatrixIfNeeded(initialMatrix, permissionContextResponse, effectiveRole);
	commitMatrixState({ matrixResponse, permissionContextResponse, ...input });
}

export function shouldSkipRefreshExecution(isRefreshingRef: MutableRefObject<boolean>, lastRefreshRef: MutableRefObject<number>): boolean {
	if (isRefreshingRef.current) {
		return true;
	}
	const now = Date.now();
	if (now - lastRefreshRef.current < REFRESH_DEBOUNCE_MS) {
		return true;
	}
	lastRefreshRef.current = now;
	return false;
}

export function resolveActiveAccessTokenForRefresh(): {
	activeContext: ReturnType<typeof resolveAuthContextForRolePermissionsRefresh>;
	activeAccessToken: string | null;
} {
	const activeContext = resolveAuthContextForRolePermissionsRefresh();
	const activeAccessToken =
		activeContext === "notary"
			? resolveNotaryAccessTokenFromSession()
			: activeContext
				? (getTokenFromConnectedStoreByContext(activeContext) ?? getLeCoffreAccessTokenCookieForContext(activeContext))
				: null;
	return { activeContext, activeAccessToken };
}

export async function refreshNotaryMatrixState(input: {
	setMatrix: (value: Matrix) => void;
	setPermissionContext: (value: PermissionContextDto | null) => void;
	setLoading: (value: boolean) => void;
	setError: (value: string | null) => void;
}): Promise<void> {
	queueMicrotask(() => {
		input.setLoading(true);
		input.setError(null);
	});
	try {
		await fetchMatrixAndCommitState(input);
	} catch (error) {
		await handleRefreshError({ error, ...input });
	}
}

export function commitClientSessionMatrixState(
	activeContext: "customer" | "thirdparty",
	activeAccessToken: string,
	input: {
		setMatrix: (value: Matrix) => void;
		setPermissionContext: (value: PermissionContextDto | null) => void;
		setLoading: (value: boolean) => void;
	},
): void {
	const sessionContext = buildSessionPermissionContext(activeContext, activeAccessToken);
	commitMatrixState({
		matrixResponse: {},
		permissionContextResponse: sessionContext,
		...input,
	});
}
